import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AlertCircle, CheckCircle, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { ColumnHeader } from "@/components/MyTable";
import SnoozeClearTriageDialog from "@/components/SnoozeClearTriageDialog";
import TableWithSearch from "@/components/TableWithSearch";
import { useAuth } from "@/contexts/use-auth";
import { useGetData } from "@/hooks/useGetData";
import AxiosInstance from "@/utils/AxiosInstance";
import {
  formatDateTime12,
  getPromise,
  getPromiseToast,
} from "@/utils/helpers";
import { toast } from "sonner";
import type { PatientV2 } from "../types";

const criticalRecordsHeaders: ColumnHeader[] = [
  { id: "caretakerDevice", label: "Device", isSortable: true },
  { id: "reading", label: "Reading", isSortable: true },
  { id: "receivedAt", label: "Received At", isSortable: true },
  { id: "status", label: "Status", isSortable: true },
];

type Props = {
  patient: PatientV2 | null;
};

export function CriticalRecordsSection({ patient }: Props) {
  const navigate = useNavigate();
  const { isAuthorized, user, profile: profileFromAuth, isLoading: authLoading } = useAuth();
  const [profile, setProfile] = useState<{ _id?: string } | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [criticalRecordsData, setCriticalRecordsData] = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSnoozeClearDialogOpen, setIsSnoozeClearDialogOpen] = useState(false);
  const [selectedAlertForSnooze, setSelectedAlertForSnooze] = useState<any>(null);
  const [btnLoading, setBtnLoading] = useState(false);

  const isCaretaker = user?.role === "caretaker";
  const patientId = patient?._id;

  useEffect(() => {
    if (!authLoading && user) {
      if (profileFromAuth) {
        setProfile(profileFromAuth as { _id?: string });
      } else {
        const fetchProfile = async () => {
          try {
            const response = await AxiosInstance().get<{ data?: { profile?: { _id?: string } } }>(
              "/auth/me",
            );
            if (response?.data?.data?.profile) {
              setProfile(response.data.data.profile);
            }
          } catch {
            // Profile optional for caretaker assignment check
          }
        };
        void fetchProfile();
      }
    }
  }, [user, profileFromAuth, authLoading]);

  const isCaretakerAssignedToPatient = () => {
    if (!isCaretaker) return true;
    const assignedCaretakerIds = patient?.assignedCaretakerIds || [];
    if (!Array.isArray(assignedCaretakerIds) || assignedCaretakerIds.length === 0) return false;
    const profileId = profile?._id;
    if (!profileId) return false;
    return assignedCaretakerIds.some((caretakerId) => {
      const id = typeof caretakerId === "string" ? caretakerId : caretakerId?._id;
      return id === profileId;
    });
  };

  const { apiData: criticalReadingsData } = useGetData(
    "/critical-readings",
    {
      search: "",
      status: "pending",
      patientId,
      refreshKey,
      page: 1,
      limit: 10,
      skip: 0,
      sortKey: "",
      sortType: "",
    },
    false,
    !!patientId,
    () => {},
  );

  const criticalRecordsCount =
    criticalReadingsData?.data?.total ||
    criticalReadingsData?.data?.alerts?.length ||
    0;

  const handleSnoozeClearTriage = (alert: any) => {
    setSelectedAlertForSnooze(alert);
    setIsSnoozeClearDialogOpen(true);
  };

  const handleSnoozeClearConfirm = async (data: {
    timePeriod?: string;
    reason?: string;
    action: "snooze" | "clear" | "false_alarm";
  }) => {
    if (!selectedAlertForSnooze?.alertId) {
      toast.error("Alert ID is missing");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setBtnLoading(true);

      if (data.action === "snooze") {
        const timePeriodMap: Record<string, number> = {
          "15 minutes": 15,
          "30 minutes": 30,
          "1 hour": 60,
        };
        const snoozeMinutes = timePeriodMap[data.timePeriod || ""];
        if (!snoozeMinutes) {
          toast.error("Invalid time period selected");
          return;
        }

        const snoozeTime = new Date(Date.now() + snoozeMinutes * 60 * 1000).toISOString();
        await AxiosInstance().patch(`/critical-readings/${selectedAlertForSnooze.alertId}/snooze`, {
          snoozeTime,
        });
        resolveFn("Alert snoozed successfully");
      } else if (data.action === "clear") {
        await AxiosInstance().patch(`/critical-readings/${selectedAlertForSnooze.alertId}/resolve`, {
          resolutionNotes: data.reason,
        });
        resolveFn("Triage cleared successfully");
      } else if (data.action === "false_alarm") {
        if (!data.reason?.trim()) {
          toast.error("Please provide a dismissal reason");
          return;
        }
        await AxiosInstance().patch(`/critical-readings/${selectedAlertForSnooze.alertId}/dismiss`, {
          dismissalReason: data.reason.trim(),
        });
        resolveFn("Alert dismissed as false alarm successfully");
      }

      setIsSnoozeClearDialogOpen(false);
      setSelectedAlertForSnooze(null);
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      rejectFn(error?.response?.data?.message || "Failed to process request");
    } finally {
      setBtnLoading(false);
    }
  };

  const handleResolve = (alert: any) => {
    setIsDialogOpen(false);
    setTimeout(() => {
      navigate("/alerts/resolve", { state: { alert } });
    }, 0);
  };

  if (!isAuthorized("canAddPatients") || criticalRecordsCount <= 0) {
    return null;
  }

  return (
    <>
      <Badge
        variant="outline"
        onClick={() => setIsDialogOpen(true)}
        className="cursor-pointer rounded-full border-red-500 px-2.5 py-0.5 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
      >
        <AlertCircle className="mr-1 h-3 w-3" />
        Critical Records {criticalRecordsCount}
      </Badge>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="!max-w-[98vw] !w-[98vw] max-h-[90vh] overflow-y-auto sm:!max-w-[98vw] md:!max-w-[98vw] lg:!max-w-[98vw] xl:!max-w-[98vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Critical Records ({criticalRecordsCount})
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 w-full">
            <TableWithSearch
              heading="Pending Critical Readings"
              isColumnSelection
              columnPreferenceKey={`patient-critical-readings-${patientId}`}
              url="/critical-readings"
              params={{
                status: "pending",
                patientId,
                refreshKey,
              }}
              apiDataKey="alerts"
              fetchCondition={isDialogOpen}
              isIncludeSearch
              searchPlaceholder="Search Patient..."
              columnHeaders={criticalRecordsHeaders}
              data={criticalRecordsData}
              viewData={criticalRecordsData.map((alert: any) => {
                const readingValues = alert?.readingValues || {};
                const readingValuesFormatted = Object.entries(readingValues)
                  .map(([key, value]) => {
                    const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
                    return `${formattedKey}: ${value}`;
                  })
                  .join(", ");

                const isSnoozed = alert?.snoozeTime && new Date(alert.snoozeTime) > new Date();
                const deviceLabel = alert?.deviceType
                  ? alert.deviceType
                      .replace(/_/g, " ")
                      .split(" ")
                      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(" ")
                  : "";

                return {
                  ...alert,
                  reading:
                    readingValuesFormatted ||
                    alert?.readingId?.readingId ||
                    alert?.readingId?._id ||
                    "",
                  caretakerDevice: isSnoozed ? (
                    <span className="flex items-center gap-2">
                      {deviceLabel}
                      <Clock className="h-4 w-4 text-primary" />
                    </span>
                  ) : (
                    deviceLabel
                  ),
                  receivedAt: alert?.createdAt
                    ? formatDateTime12(alert.createdAt)
                    : alert?.readingId?.readingDate
                      ? formatDateTime12(alert.readingId.readingDate)
                      : "",
                  status: isSnoozed ? "Snoozed" : alert?.status?.replace(/_/g, " ") || "",
                };
              })}
              setData={setCriticalRecordsData}
              isActionColumn={false}
              filtersOptions={[]}
              handleAction={() => {}}
              filters={{}}
              setFilters={() => {}}
              clickRow={() => {}}
              isDownloadData
              downloadAccessModule="canAccessPatients"
              isSelectable={false}
              selectActions={[]}
              donotFetch={false}
              isIgnoreSearchParams
              moreActions={[
                {
                  label: "Snooze/Clear Triage",
                  icon: <Clock className="h-4 w-4" />,
                  onClick: handleSnoozeClearTriage,
                  isShow: (alert: any) => alert?.status === "pending",
                  isDisabled: (alert: any) => {
                    const isSnoozed = alert?.snoozeTime && new Date(alert.snoozeTime) > new Date();
                    if (isCaretaker) return isSnoozed || !isCaretakerAssignedToPatient();
                    return isSnoozed;
                  },
                },
                {
                  label: "Resolve",
                  icon: <CheckCircle className="h-4 w-4" />,
                  onClick: handleResolve,
                  isShow: (alert: any) => alert?.status === "pending",
                  isDisabled: (alert: any) => {
                    const isSnoozed = alert?.snoozeTime && new Date(alert.snoozeTime) > new Date();
                    if (isCaretaker) return isSnoozed || !isCaretakerAssignedToPatient();
                    return isSnoozed;
                  },
                },
              ]}
            />
          </div>
        </DialogContent>
      </Dialog>

      <SnoozeClearTriageDialog
        isOpen={isSnoozeClearDialogOpen}
        onClose={() => {
          setIsSnoozeClearDialogOpen(false);
          setSelectedAlertForSnooze(null);
        }}
        onConfirm={handleSnoozeClearConfirm}
        isLoading={btnLoading}
      />
    </>
  );
}
